<template>
  <v-row class="ma-0">
    <!-- loop sections -->
    <v-col
      v-for="section in loanSections"
      :key="section.code"
      cols="12"
      class="py-3"
    >
      <div class="assumption-card">
        <div class="assumption-title">
          {{ section.label }}
        </div>

        <v-row
          class="ma-0"
        >
          <v-col
            v-for="(m, index) in months"
            :key="`${section.code}-${m}`"
            cols="12"
            md="6"
            :class="`pa-0 ${index % 2 === 0 ? 'pr-md-10' : ''} py-3`"
          >
            <div class="d-flex flex-row align-center justify-space-between">
              <div class="row-title">
                {{ m }} months
              </div>
              <v-text-field
                :value="loanModel[section.code][m].rate"
                type="number"
                :step="0.01"
                hide-details
                dense
                outlined
                style="background: white; border: 1px solid var(--v-synthesis-ui-grey-02-base);font-weight: 400;"
                class="pct-input"
                :suffix="'%'"
                @input="updateLoanRate(section.code, m, $event)"
              />
            </div>

            <v-slider
              :value="loanModel[section.code][m].rate"
              :min="loanModel[section.code][m].minRate"
              :max="loanModel[section.code][m].maxRate"
              :step="0.01"
              :color="getSliderColor(section.code, loanModel[section.code][m].minRate)"
              :track-color="getSliderTrackColor(section.code, loanModel[section.code][m].minRate)"
              hide-details
              class="flex-grow-1 slider-input"
              :style="getSliderStyle(section.code, loanModel[section.code][m].minRate)"
              @input="updateLoanRate(section.code, m, $event)"
            />
          </v-col>
        </v-row>
      </div>
    </v-col>

    <!-- ===== Deposit Reinvestment Spread (optional; e.g., TCD) ===== -->
    <v-col
      v-if="reinvestmentSections && reinvestmentSections.length"
      cols="12"
      class="py-3"
    >
      <div class="assumption-card">
        <div class="assumption-title">
          {{ $t('Assumptions.EditAssumptions.Tabs.LoanAssumptions.LoansReinvestmentSpread') }}
        </div>

        <v-row class="ma-0">
          <v-col
            v-for="(cat, index) in reinvestmentSections"
            :key="'spread-' + cat.code"
            cols="12"
            md="6"
            :class="`pa-0 ${index % 2 === 0 ? 'pr-md-10' : ''} py-3`"
          >
            <div class="d-flex flex-row align-center justify-space-between">
              <div class="row-title">
                {{ cat.label }}
              </div>
              <v-text-field
                :value="reinvestmentModel[cat.code].spread"
                type="number"
                :step="0.01"
                hide-details
                dense
                outlined
                style="background:white;border:1px solid var(--v-synthesis-ui-grey-02-base);font-weight:400;"
                class="pct-input"
                :suffix="'%'"
                @input="updateReinvestment(cat.code, 'spread', $event)"
              />
            </div>

            <v-slider
              :value="reinvestmentModel[cat.code].spread"
              :min="reinvestmentModel[cat.code].minSpread"
              :max="reinvestmentModel[cat.code].maxSpread"
              :step="0.01"
              :color="getSliderColor('spread', reinvestmentModel[cat.code].minSpread)"
              :track-color="getSliderTrackColor('spread', reinvestmentModel[cat.code].minSpread)"
              hide-details
              class="flex-grow-1 slider-input"
              :style="getSliderStyle('spread', reinvestmentModel[cat.code].minSpread)"
              @input="updateReinvestment(cat.code, 'spread', $event)"
            />
          </v-col>
        </v-row>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import chartsColorMixin from '@/theme/default/components/charts/mixins/chartsColorMixin'

export default {
  name  : 'LoanAssumptions',
  mixins: [chartsColorMixin],
  props : {
    loanSections: {
      type    : Array,
      required: true
    },
    months: {
      type    : Array,
      required: true
    },
    loanModel: {
      type    : Object,
      required: true
    },
    reinvestmentSections: { type: Array, default: () => [] },
    reinvestmentModel   : { type: Object, default: () => ({}) }

  },
  methods: {
    colorByCategory (code) {
      if (code === 'CRE') return this.themeColorRGBA('synthesis-ui-green-02')
      if (code === 'ARM') return this.themeColorRGBA('synthesis-ui-yellow-01')
      if (code === 'FRM') return this.themeColorRGBA('synthesis-ui-blue-04')
      return this.themeColorRGBA('synthesis-ui-grey-06')
    },
    colorByGroup (group) {
      if (group === 'spread') return this.themeColorRGBA('synthesis-ui-red-05')
      return this.themeColorRGBA('synthesis-ui-grey-06')
    },

    // Check if range is negative (min < 0)
    isNegativeRange (minValue) {
      return minValue < 0
    },

    // Get slider color - reversed when range is negative
    getSliderColor (categoryCode, minValue) {
      // Handle special case for 'spread' group
      if (categoryCode === 'spread') {
        return this.isNegativeRange(minValue) ? this.themeColorRGBA('synthesis-ui-grey-06') : this.themeColorRGBA('synthesis-ui-blue-04')
      }
      // Handle loan categories
      const baseColor = this.colorByCategory(categoryCode)
      return this.isNegativeRange(minValue) ? this.themeColorRGBA('synthesis-ui-grey-06') : baseColor
    },

    // Get track color - reversed when range is negative
    getSliderTrackColor (categoryCode, minValue) {
      // Handle special case for 'spread' group
      if (categoryCode === 'spread') {
        return this.isNegativeRange(minValue) ? this.colorByGroup('spread') : this.themeColorRGBA('synthesis-ui-grey-06')
      }
      // Handle loan categories
      const baseColor = this.colorByCategory(categoryCode)
      return this.isNegativeRange(minValue) ? baseColor : this.themeColorRGBA('synthesis-ui-grey-06')
    },

    // Get slider style - reversed when range is negative
    getSliderStyle (categoryCode, minValue) {
      // Handle special case for 'spread' group
      if (categoryCode === 'spread') {
        const color = this.isNegativeRange(minValue) ? this.themeColorRGBA('synthesis-ui-grey-06') : this.themeColorRGBA('synthesis-ui-blue-04')
        const thumbColor = this.isNegativeRange(minValue) ? this.colorByGroup('spread') : this.themeColorRGBA('synthesis-ui-blue-04')
        return {
          '--c'          : color,
          '--glow'       : this.withAlpha(color, 0.18),
          '--thumb-color': thumbColor,
          '--thumb-glow' : this.withAlpha(thumbColor, 0.32)
        }
      }
      // Handle loan categories
      const baseColor = this.colorByCategory(categoryCode)
      const color = this.isNegativeRange(minValue) ? this.themeColorRGBA('synthesis-ui-grey-06') : baseColor
      const thumbColor = this.isNegativeRange(minValue) ? baseColor : baseColor
      return {
        '--c'          : color,
        '--glow'       : this.withAlpha(color, 0.18),
        '--thumb-color': thumbColor,
        '--thumb-glow' : this.withAlpha(thumbColor, 0.32)
      }
    },

    // slider styling vars (thumb glow etc.)
    sliderStyle (color) {
      return {
        '--c'   : color,
        '--glow': this.withAlpha(color, 0.18)
      }
    },
    withAlpha (rgbaOrHex, a = 0.18) {
      // accepts rgba(...) or #hex; returns rgba with alpha
      if (rgbaOrHex.startsWith('rgba')) {
        return rgbaOrHex.replace(/rgba\(([^)]+)\)/, (_, rgb) => `rgba(${rgb.split(',').slice(0, 3).join(',')}, ${a})`)
      }
      const hex = rgbaOrHex.replace('#', '')
      const h = hex.length === 3 ? hex.split('').map(x => x + x).join('') : hex
      const n = parseInt(h, 16)
      const r = (n >> 16) & 255; const g = (n >> 8) & 255; const b = n & 255
      return `rgba(${r}, ${g}, ${b}, ${a})`
    },
    updateLoanRate (categoryCode, month, value) {
      this.$emit('update-loan-rate', { categoryCode, month, value: Number(value) })
    },
    updateReinvestment (categoryCode, field, value) {
      this.$emit('update-reinvestment', { categoryCode, field, value: Number(value) })
    }
  }
}
</script>

<style scoped>
.assumption-card{
  border: 1px solid #E6EEF5;
  background: #FFFFFF;
  border-radius: 12px;
  padding: 16px;
}
.assumption-title{
  font-size: 14px;
  font-weight: 600;
  color: #63747D;
  margin-bottom: 10px;
}
.row-title{
  font-size: 13px;
  font-weight: 600;
  color: #041F30;
  margin-bottom: 4px;
}

.pct-input{
  max-width: 95px;
  input {
    color: var(--v-synthesis-ui-grey-01-base);
  }
}

/* Hide number input spinners */
::v-deep .pct-input input[type="number"]::-webkit-outer-spin-button,
::v-deep .pct-input input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  appearance: none;
  margin: 0;
}

/* Hide number input spinners in Firefox */
::v-deep .pct-input input[type="number"] {
  appearance: textfield;
  -moz-appearance: textfield;
}

/* Style the suffix color */
::v-deep .pct-input .v-input__suffix,
::v-deep .pct-input .v-input__suffix .v-input__suffix-inner,
::v-deep .pct-input .v-input__append-inner {
  color: var(--v-synthesis-ui-grey-01-base) !important;
}

/* Alternative approach - target the suffix text directly */
::v-deep .pct-input .v-text-field__suffix {
  color: var(--v-synthesis-ui-grey-01-base) !important;
}

/* Track height + rounded pill look */
::v-deep .slider-input .v-slider__track-background,
::v-deep .slider-input .v-slider__track-fill {
  height: 10px;                /* thicker track */
  border-radius: 6px;
}

/* Background track: white with subtle border and inset highlight */
::v-deep .slider-input .v-slider__track-background {               /* light pill */
  border: 1px solid #DDE6F1;                      /* subtle border */
  box-shadow: inset 0 1px 0 rgba(0,0,0,0.02);     /* tiny inner highlight */
}

::v-deep .slider-input .v-slider__track-fill {
  border-radius: 2px;
  height: 10px;
}

::v-deep .slider-input .v-slider__thumb {
  margin-top: 4px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid #FFFFFF !important;                       /* white ring */
  background-color: var(--thumb-color, #3D68D5) !important;   /* use custom thumb color */
  box-shadow: 0px 0px 8px 0px var(--thumb-glow, rgba(61, 104, 213, 0.32)) !important;
  /* colored glow */
}

/* Remove default focus halo so our glow stands out */
::v-deep .slider-input .v-slider__thumb::before {
  display: none;
}

/* Optional: tighter container alignment */
::v-deep .slider-input .v-input__control,
::v-deep .slider-input .v-input__slot {
  margin: 0;
}

</style>
